// Two imports to keep the IIFE small: the keys file is Zod-free and the
// `type` import is erased at runtime. Together they pull in zero runtime
// dependencies from @markku/types — the IIFE is just the capture logic + 19
// string constants. See package exports in @markku/types/package.json.
import {
  TRACKED_CLICK_ID_KEYS,
  TRACKED_QUERY_KEYS,
  TRACKED_UTM_KEYS,
  type TrackedClickIdKey,
} from '@markku/types/acquisition-keys'
import type { AcquisitionData } from '@markku/types/acquisition'

// Canonical allowlist for landingPage scrub (INV-6). Anything not in this set
// is stripped before write so one-time tokens (magic-link, password-reset,
// invoice-preview) don't get persisted in the mk_attr cookie or in downstream
// internal*Acquisition* DB columns.
const LANDING_PAGE_ALLOWED_KEYS = new Set<string>(TRACKED_QUERY_KEYS)

function sanitizeLandingPage(href: string): string {
  try {
    const url = new URL(href)
    const keep = new URLSearchParams()
    url.searchParams.forEach((value, key) => {
      if (LANDING_PAGE_ALLOWED_KEYS.has(key)) keep.set(key, value)
    })
    url.search = keep.toString() ? `?${keep.toString()}` : ''
    url.hash = ''
    return url.toString()
  } catch {
    // Malformed href — return the origin+pathname fallback to avoid leaking
    // anything from the (broken) input.
    return href.split('?')[0]
  }
}

export function captureAcquisition(): AcquisitionData {
  const params = new URLSearchParams(window.location.search)

  const utmParams: Record<string, string> = {}
  for (const key of TRACKED_UTM_KEYS) {
    const v = params.get(key)
    if (v) utmParams[key] = v
  }

  const clickIds: Partial<Record<TrackedClickIdKey, string>> = {}
  for (const key of TRACKED_CLICK_ID_KEYS) {
    const v = params.get(key)
    if (v) clickIds[key] = v
  }

  return {
    ...(Object.keys(utmParams).length ? { utmParams } : {}),
    ...clickIds,
    ...(document.referrer ? { referrer: document.referrer } : {}),
    landingPage: sanitizeLandingPage(window.location.href),
    timestamp: new Date().toISOString(),
    device: {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  }
}

// A "touch" is an identifiable channel impression: a tracked click ID/UTM,
// or a referrer from a different host (external site linked to us).
// Returning users with no tracking in the URL are NOT new touches.
export function isNewTouch(data: AcquisitionData): boolean {
  if (data.utmParams && Object.keys(data.utmParams).length) return true
  for (const key of TRACKED_CLICK_ID_KEYS) {
    if (data[key]) return true
  }
  if (data.referrer) {
    try {
      const refHost = normalizeHost(new URL(data.referrer).host)
      if (refHost && refHost !== normalizeHost(window.location.host)) return true
    } catch {
      // Malformed referrer — ignore
    }
  }
  return false
}

// Collapse to the registrable domain so go.markku.fi, www.markku.fi, and
// markku.fi all bucket together (matches the cookie's Domain attribute).
// Uses the last two labels — consistent with getCookieDomain, and sufficient
// for the single-label TLDs we ship on (.fi, .com).
function normalizeHost(host: string): string {
  const lower = host.toLowerCase()
  const parts = lower.split('.')
  if (parts.length < 2) return lower
  return parts.slice(-2).join('.')
}
